import axios from "axios";

import type { ApiError } from "./ApiError";
import { isApiError } from "./ApiError";

type ApiErrorBody = {
  code?: unknown;
  message?: unknown;
  details?: unknown;
};

const extractFields = (
  details: unknown,
): Record<string, string[]> | undefined => {
  if (!details || typeof details !== "object") return undefined;
  const fields = (details as { fields?: unknown }).fields;
  if (!fields || typeof fields !== "object" || Array.isArray(fields))
    return undefined;

  const normalized = Object.entries(fields).reduce<Record<string, string[]>>(
    (result, [name, messages]) => {
      if (
        Array.isArray(messages) &&
        messages.every((message) => typeof message === "string")
      ) {
        result[name] = messages;
      }
      return result;
    },
    {},
  );
  return Object.keys(normalized).length ? normalized : undefined;
};

export const normalizeApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const body = error.response?.data as ApiErrorBody | undefined;
    const details = body?.details;
    const timedOut =
      error.code === "ECONNABORTED" ||
      error.code === "ETIMEDOUT" ||
      status === 408 ||
      status === 504;
    const fallbackCode = timedOut
      ? "REQUEST_TIMEOUT"
      : !error.response
        ? "NETWORK_ERROR"
        : status && status >= 500
          ? "SERVER_ERROR"
          : "UNKNOWN_API_ERROR";
    const fallbackMessage = timedOut
      ? "サーバーの応答がタイムアウトしました。しばらくしてからもう一度お試しください。"
      : !error.response
        ? "サーバーに接続できません。"
        : "リクエストに失敗しました。";
    return {
      status,
      code: typeof body?.code === "string" ? body.code : fallbackCode,
      message:
        typeof body?.message === "string" ? body.message : fallbackMessage,
      fields: extractFields(details),
      details,
      cause: error,
    };
  }

  if (isApiError(error)) return error;

  return {
    code: "UNKNOWN_ERROR",
    message:
      error instanceof Error ? error.message : "不明なエラーが発生しました。",
    cause: error,
  };
};
